/**
 * Floating supervisor-reaction overlay.
 *
 * Listens for the `hbf:coach-reaction` window event (emitted by
 * RTCContext when the backend pushes a `coach-reaction` over the agent's
 * WebSocket) and renders a soft, non-blocking emoji card in the bottom-right
 * corner of the agent's screen. The card auto-dismisses after 4.5s; if more
 * reactions come in, they stack and animate independently.
 *
 * Zero audio — entirely visual — so the live call audio isn't disrupted.
 */

import { useEffect, useState } from "react";

const DUR_MS = 4500;

export default function CoachReactionOverlay() {
  const [items, setItems] = useState([]); // { id, emoji, note, from, at }

  useEffect(() => {
    function onReaction(e) {
      const d = e.detail || {};
      const item = {
        id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        emoji: d.emoji || "✨",
        note: d.note || "",
        from: d.from_supervisor_name || "Supervisor",
        reaction: d.reaction || "",
      };
      // Keep the visible stack capped to the last 5 cards so spam can't
      // overflow the viewport. Older cards stop animating in.
      setItems((arr) => [...arr, item].slice(-5));
      setTimeout(() => setItems((arr) => arr.filter((x) => x.id !== item.id)), DUR_MS);
    }
    window.addEventListener("hbf:coach-reaction", onReaction);
    return () => window.removeEventListener("hbf:coach-reaction", onReaction);
  }, []);

  if (items.length === 0) return null;

  return (
    <div
      className="fixed bottom-24 right-6 flex flex-col-reverse gap-2 z-[9999] pointer-events-none"
      data-testid="coach-reaction-stack"
    >
      {items.map((it) => (
        <div
          key={it.id}
          className="bg-[#0F172A] text-white px-4 py-3 shadow-2xl border border-[#1E293B] flex items-center gap-3 animate-coach-reaction"
          style={{ minWidth: 220, maxWidth: 320 }}
          data-testid={`coach-reaction-${it.reaction}`}
        >
          <div className="text-3xl leading-none" aria-hidden="true">{it.emoji}</div>
          <div className="flex-1">
            <div className="font-mono text-[10px] tracking-widest text-[#67E8F9]">/ SUPERVISOR REACTION</div>
            <div className="text-[13px] font-semibold">{it.from}</div>
            {it.note ? <div className="text-[11px] text-[#94A3B8] mt-0.5">{it.note}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}
